import React from 'react';
import {
  Globe,
  FileText,
  GitBranch,
  Shield,
  ArrowRight,
  Code,
  AlertTriangle,
  CheckCircle,
  Workflow,
  Network,
  Database,
  RefreshCw,
  MessageSquare
} from 'lucide-react';

const Section = ({ title, icon: Icon, children, className = "" }) => (
  <div className={`mb-8 bg-white rounded-xl border border-gray-200 ${className}`}>
    <div className="p-6 border-b border-gray-100">
      <div className="flex items-center">
        <Icon className="w-6 h-6 text-blue-600 mr-3" />
        <h2 className="text-xl font-semibold">{title}</h2>
      </div>
    </div>
    <div className="p-6">{children}</div>
  </div>
);

const InfoBlock = ({ title, description, icon: Icon, variant = "default" }) => {
  const variants = {
    default: "bg-blue-50 border-blue-200",
    warning: "bg-yellow-50 border-yellow-200",
    success: "bg-green-50 border-green-200",
    error: "bg-red-50 border-red-200"
  };

  return (
    <div className={`p-4 rounded-lg border ${variants[variant]}`}>
      <div className="flex items-center mb-2">
        <Icon className="w-5 h-5 mr-2" />
        <h3 className="font-medium">{title}</h3>
      </div>
      <div className="text-sm space-y-2">{description}</div>
    </div>
  );
};

const MessageFormatExample = ({ title, format }) => (
  <div className="bg-gray-900 rounded-lg p-4">
    <div className="flex justify-between items-center mb-3">
      <span className="text-gray-400 text-sm">{title}</span>
      <Code className="w-4 h-4 text-gray-500" />
    </div>
    <pre className="text-sm text-gray-300 font-mono whitespace-pre-wrap">
      <code>{format}</code>
    </pre>
  </div>
);

const CBPRPlusAnalysis = () => {
  // Définition et contexte
  const overview = {
    description: "Standard de messagerie ISO 20022 pour paiements transfrontaliers",
    scope: [
      "Messages de paiement client (pacs)",
      "Messages de reporting (camt)",
      "Messages de gestion (admi)",
      "Messages de statut (pain)"
    ],
    benefits: [
      "Interopérabilité accrue",
      "Données enrichies",
      "Traitement STP optimisé",
      "Conformité réglementaire"
    ]
  };

  // Spécifications techniques
  const technicalSpecs = {
    messageTypes: [
      {
        type: "pacs.008",
        name: "FIToFICustomerCreditTransfer",
        usage: "Transfert crédit client",
        details: [
          "Business Application Header (BAH)",
          "Références transactions",
          "Informations parties",
          "Détails règlement"
        ]
      },
      {
        type: "pacs.009",
        name: "FinancialInstitutionCreditTransfer",
        usage: "Transfert interbancaire",
        details: [
          "Business Application Header (BAH)",
          "Identifiants banques",
          "Instructions règlement",
          "Codes services"
        ]
      },
      {
        type: "camt.053",
        name: "BankToCustomerStatement",
        usage: "Relevé compte",
        details: [
          "Soldes comptes",
          "Détails transactions",
          "Informations référence",
          "Statuts opérations"
        ]
      }
    ],
    validationRules: [
      {
        category: "Structure",
        rules: [
          "Schema XSD validation",
          "Mandatory fields presence",
          "Field format compliance",
          "Cross-field validation"
        ]
      },
      {
        category: "Business",
        rules: [
          "Party identification rules",
          "Amount format rules",
          "Character set restrictions",
          "Purpose code validation"
        ]
      }
    ]
  };

  // Exemples de messages
  const messageExamples = {
    pacs008: `<?xml version="1.0" encoding="UTF-8"?>
<Document xmlns="urn:iso:std:iso:20022:tech:xsd:pacs.008.001.08">
  <FIToFICstmrCdtTrf>
    <GrpHdr>
      <MsgId>BANK1/20240325/1234</MsgId>
      <CreDtTm>2024-03-25T10:30:00</CreDtTm>
      <NbOfTxs>1</NbOfTxs>
    </GrpHdr>
    <CdtTrfTxInf>
      <PmtId>
        <InstrId>BANK1/12345</InstrId>
        <EndToEndId>E2E-REF/6789</EndToEndId>
      </PmtId>
      <!-- ... autres éléments ... -->
    </CdtTrfTxInf>
  </FIToFICstmrCdtTrf>
</Document>`,
    camt053: `<?xml version="1.0" encoding="UTF-8"?>
<Document xmlns="urn:iso:std:iso:20022:tech:xsd:camt.053.001.08">
  <BkToCstmrStmt>
    <GrpHdr>
      <MsgId>BANK1/STMT/20240325</MsgId>
      <CreDtTm>2024-03-25T18:00:00</CreDtTm>
    </GrpHdr>
    <Stmt>
      <Id>2024/123</Id>
      <LglSeqNb>123</LglSeqNb>
      <!-- ... autres éléments ... -->
    </Stmt>
  </BkToCstmrStmt>
</Document>`
  };

  // Impacts et exigences
  const implementationRequirements = {
    technical: [
      {
        category: "Infrastructure",
        requirements: [
          "Parsing XML performant",
          "Validation messages temps réel",
          "Stockage données enrichies",
          "Monitoring & reporting"
        ]
      },
      {
        category: "Sécurité",
        requirements: [
          "Chiffrement bout en bout",
          "Signature digitale messages",
          "Audit trail complet",
          "Gestion identités"
        ]
      }
    ],
    operational: [
      {
        category: "Process",
        requirements: [
          "Workflows validation",
          "Gestion exceptions",
          "Réconciliation automatique",
          "Support 24/7"
        ]
      },
      {
        category: "Conformité",
        requirements: [
          "Piste audit complète",
          "Reporting réglementaire",
          "KYC/AML contrôles",
          "Data privacy"
        ]
      }
    ]
  };

  // Challenges et solutions
  const challenges = [
    {
      category: "Migration",
      items: [
        {
          challenge: "Coexistence MT/MX",
          impact: "Élevé",
          solution: "Translation engines + validation croisée"
        },
        {
          challenge: "Données enrichies",
          impact: "Moyen",
          solution: "Data mapping intelligent et enrichissement"
        }
      ]
    },
    {
      category: "Technique",
      items: [
        {
          challenge: "Performance processing",
          impact: "Élevé",
          solution: "Optimisation XML + caching"
        },
        {
          challenge: "Validation temps réel",
          impact: "Élevé",
          solution: "Validation asynchrone distribuée"
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">
            CBPR+ (Cross-Border Payments & Reporting Plus)
          </h1>
          <p className="text-lg text-gray-600">
            Analyse détaillée du standard ISO 20022 pour paiements transfrontaliers
          </p>
        </div>

        {/* Aperçu */}
        <Section title="Aperçu" icon={Globe}>
          <div className="space-y-6">
            <p className="text-gray-700">{overview.description}</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-medium mb-3">Périmètre</h3>
                <ul className="space-y-2">
                  {overview.scope.map((item, idx) => (
                    <li key={idx} className="flex items-center text-sm">
                      <ArrowRight className="w-4 h-4 mr-2 text-blue-500" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="font-medium mb-3">Bénéfices</h3>
                <ul className="space-y-2">
                  {overview.benefits.map((item, idx) => (
                    <li key={idx} className="flex items-center text-sm">
                      <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </Section>

        {/* Spécifications */}
        <Section title="Spécifications Techniques" icon={FileText}>
          <div className="space-y-6">
            {/* Types de messages */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {technicalSpecs.messageTypes.map((msg, idx) => (
                <div key={idx} className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center mb-3">
                    <MessageSquare className="w-5 h-5 text-blue-500 mr-2" />
                    <h3 className="font-medium">{msg.type}</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">{msg.name}</p>
                  <p className="text-sm text-gray-500 mb-3">{msg.usage}</p>
                  <ul className="space-y-1">
                    {msg.details.map((detail, i) => (
                      <li key={i} className="text-sm flex items-start">
                        <span className="text-blue-500 mr-2">•</span>
                        {detail}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* Exemples */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-medium mb-3">pacs.008 Example</h3>
                <MessageFormatExample 
                  title="Customer Credit Transfer"
                  format={messageExamples.pacs008}
                />
              </div>
              <div>
                <h3 className="font-medium mb-3">camt.053 Example</h3>
                <MessageFormatExample 
                  title="Bank Statement"
                  format={messageExamples.camt053}
                />
              </div>
            </div>

            {/* Règles de validation */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {technicalSpecs.validationRules.map((rule, idx) => (
                <div key={idx} className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-medium mb-3">{rule.category} Rules</h3>
                  <ul className="space-y-2">
                    {rule.rules.map((r, i) => (
                      <li key={i} className="flex items-center text-sm">
                        <Shield className="w-4 h-4 mr-2 text-blue-500" />
                        {r}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </Section>

        {/* Exigences Implémentation */}
        <Section title="Exigences d'Implémentation" icon={GitBranch}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {implementationRequirements.technical.concat(
              implementationRequirements.operational
            ).map((req, idx) => (
              <div key={idx} className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium mb-3">{req.category}</h3>
                <ul className="space-y-2">
                  {req.requirements.map((r, i) => (
                    <li key={i} className="flex items-center text-sm">
                      <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                      {r}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Section>

        {/* Challenges */}
        <Section title="Challenges & Solutions" icon={AlertTriangle}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {challenges.map((cat, idx) => (
              <div key={idx} className="space-y-4">
                <h3 className="font-medium">{cat.category}</h3>
                {cat.items.map((item, i) => (
                  <InfoBlock
                    key={i}
                    title={item.challenge}
                    icon={AlertTriangle}
                    variant={item.impact === 'Élevé' ? 'warning' : 'default'}
                    description={
                      <div>
                        <div className="mb-2">
                          <span className={`inline-block px-2 py-1 text-xs rounded ${
                            item.impact === 'Élevé' 
                              ? 'bg-red-100 text-red-700'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            Impact: {item.impact}
                          </span>
                        </div>
                        <p className="text-gray-600">
                          Solution: {item.solution}
                        </p>
                      </div>
                    }
                  />
                ))}
              </div>
            ))}
          </div>
        </Section>

        {/* Recommandations */}
        <Section title="Recommandations" icon={CheckCircle}>
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <InfoBlock
                title="Approche Migration"
                icon={RefreshCw}
                description={
                  <ul className="space-y-2">
                    <li>Migration progressive par phases</li>
                    <li>Tests parallèles MT/MX</li>
                    <li>Validation end-to-end</li>
                    <li>Formation équipes</li>
                  </ul>
                }
              />
              <InfoBlock
                title="Architecture Technique"
                icon={Network}
                description={
                  <ul className="space-y-2">
                    <li>Design microservices</li>
                    <li>Validation asynchrone</li>
                    <li>Monitoring temps réel</li>
                    <li>Scalabilité horizontale</li>
                  </ul>
                }
              />
              <InfoBlock
                title="Gouvernance"
                icon={Shield}
                description={
                  <ul className="space-y-2">
                    <li>Comité de pilotage dédié</li>
                    <li>Roadmap claire</li>
                    <li>KPIs mesurables</li>
                    <li>Review régulière</li>
                  </ul>
                }
              />
            </div>
          </div>
        </Section>
      </div>
    </div>
  );
};

export default CBPRPlusAnalysis;